// Home: the guided next move comes first. For each exam the student has, the
// brain says the single most useful thing to do next, with a target and a
// per-skill readiness read underneath. This is the difference between a
// content library and a guided score-improvement system.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Compass, Target as TargetIcon } from 'lucide-react';

import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import AppShell from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const EXAM_NAME = { celpip: 'CELPIP General', pte: 'PTE Core' };
// CLB 10 is the practical ceiling for immigration, CRS points plateau there, so
// 11/12 aren't targets anyone optimizes toward. 5 covers trades / lower-TEER routes.
const CLB_CHOICES = [5, 6, 7, 8, 9, 10];

const READY_TONE = {
  'On target': 'bg-emerald-50 text-emerald-700',
  'Near target': 'bg-indigo-50 text-indigo-700',
  'Building accuracy': 'bg-indigo-50 text-indigo-700',
  'Learning the mechanics': 'bg-amber-50 text-amber-700',
  'Responses submitted': 'bg-slate-100 text-slate-600',
  'Just getting started': 'bg-slate-100 text-slate-500',
  'Not started': 'bg-slate-100 text-slate-500',
};

const STATUS_DOT = {
  not_started: 'bg-slate-200',
  practising: 'bg-amber-400',
  on_target: 'bg-emerald-500',
  covered: 'bg-emerald-500',
};
const STATUS_WORD = { not_started: 'not started', practising: 'practising', on_target: 'on target', covered: 'done' };

// The map: every required task per skill, and how far through it the student is.
// They always see the whole exam and their coverage, nothing is hidden.
function CoverageMap({ coverage }) {
  if (!coverage?.length) return null;
  const done = coverage.reduce((a, s) => a + s.done, 0);
  const total = coverage.reduce((a, s) => a + s.total, 0);
  return (
    <div className="border-t border-slate-100 px-6 py-5" data-testid="coverage-map">
      <div className="flex items-center justify-between">
        <p className="mono-stat">your coverage</p>
        <p className="text-xs tabular-nums text-slate-500">{done} of {total} tasks on target</p>
      </div>
      <div className="mt-3 space-y-3.5">
        {coverage.map((s) => {
          const pct = s.total ? Math.round((s.done / s.total) * 100) : 0;
          return (
            <div key={s.section} data-testid={`coverage-${s.section}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium capitalize text-slate-800">{s.section}</span>
                <span className="text-xs tabular-nums text-slate-500">{s.done}/{s.total} on target</span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {s.tasks.map((t) => (
                  <span key={t.task_type} title={`${t.title}, ${STATUS_WORD[t.status]}`}
                    className={cn('h-2 w-2 rounded-full', STATUS_DOT[t.status] || 'bg-slate-200')} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ExamMove({ exam }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [res, asg] = await Promise.all([
        api.nextAction(exam),
        api.assignment(exam).catch(() => ({ data: {} })),
      ]);
      setData(res.data);
      setAssignment(asg.data?.assignment || null);
    } catch {
      setData({ error: true });
    }
  }, [exam]);

  useEffect(() => { load(); }, [load]);

  const chooseTarget = async (clb) => {
    setSaving(true);
    try { await api.setTarget(exam, clb); await load(); } finally { setSaving(false); }
  };

  if (!data) return <div className="border border-slate-200 bg-white p-8 text-slate-400">Loading…</div>;
  if (data.error) return null;

  const a = data.action;
  const isReady = a.kind === 'mock';
  const needsTarget = a.kind === 'set_target';

  return (
    <div className="border border-slate-200 bg-white" data-testid={`move-${exam}`}>
      <div className="flex items-center justify-between border-b border-slate-100 px-6 py-3">
        <p className="font-display text-[15px] font-semibold text-slate-900">{EXAM_NAME[exam] || exam.toUpperCase()}</p>
        {data.target_clb ? (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <TargetIcon className="h-3.5 w-3.5" /> Target CLB {data.target_clb}
            <span className="text-slate-300">·</span>
            {CLB_CHOICES.map((c) => (
              <button key={c} type="button" onClick={() => chooseTarget(c)}
                className={cn('px-1 tabular-nums', c === data.target_clb ? 'font-semibold text-indigo-600' : 'text-slate-400 hover:text-slate-700')}>
                {c}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {assignment && (
        <div className="border-b border-amber-100 bg-amber-50 px-6 py-3" data-testid={`assignment-${exam}`}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">Set as your priority</p>
          <div className="mt-0.5 flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-slate-800">{assignment.headline}</p>
            {assignment.cta_path && (
              <button type="button" onClick={() => navigate(assignment.cta_path)} className="shrink-0 text-sm font-semibold text-amber-700 hover:text-amber-900">
                Go <ArrowRight className="inline h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {assignment.note && <p className="mt-0.5 text-xs text-slate-600">{assignment.note}</p>}
        </div>
      )}

      <div className="px-6 py-6" data-testid="next-action">
        <p className={cn('text-[11px] font-semibold uppercase tracking-wider', isReady ? 'text-emerald-600' : 'text-indigo-600')}>
          {isReady ? 'You are ready to test yourself' : needsTarget ? 'Start here' : 'Your next move'}
        </p>
        <h2 className="h-display mt-1 text-2xl">{a.headline}</h2>
        <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-slate-600">{a.reason}</p>

        {needsTarget ? (
          <div className="mt-5">
            <p className="mono-stat mb-2">choose your target</p>
            <div className="flex flex-wrap gap-2">
              {CLB_CHOICES.map((c) => (
                <button key={c} type="button" disabled={saving} onClick={() => chooseTarget(c)} data-testid={`target-${exam}-${c}`}
                  className="border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-indigo-600 hover:text-indigo-700">
                  CLB {c}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <Button
            className={cn('mt-5', isReady ? 'inline-flex items-center gap-2 bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700' : 'cta')}
            data-testid={`move-cta-${exam}`}
            onClick={() => navigate(a.cta_path)}
          >
            {a.cta_label} <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      <CoverageMap coverage={data.coverage} />

      {data.readiness?.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t border-slate-100 px-6 py-4">
          {data.readiness.map((r) => (
            <span key={r.section} className={cn('rounded px-2.5 py-1 text-xs font-medium', READY_TONE[r.label] || 'bg-slate-100 text-slate-600')}>
              <span className="capitalize">{r.section}</span>: {r.label}{r.accuracy != null ? ` · ${Math.round(r.accuracy * 100)}%` : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useApp();

  return (
    <AppShell>
      <p className="mono-stat">Signed in as {user.name}</p>
      <h1 className="h-display mt-3 max-w-2xl text-4xl leading-[1.05]">
        Cover every task. Always know where you stand.
      </h1>
      <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-slate-500">
        Pick your target, then work through every task in the exam. Your map shows what you've covered and
        what's left for each skill, and the coach points you at the most useful next step.
      </p>

      <div className="mt-8 space-y-6">
        {(user.exams || []).length === 0 ? (
          ['org_admin', 'instructor'].includes(user.role) ? (
            <div className="border border-indigo-200 bg-indigo-50/40 p-8" data-testid="dash-staff-empty">
              <p className="font-display text-[17px] font-semibold text-slate-900">You run an organization here.</p>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-500">
                This study view is for test-takers. {user.role === 'org_admin' ? 'Add instructors and students, and issue access codes,' : 'Add students and track their readiness'} from your organization dashboard.
              </p>
              <Link to="/org" className="cta mt-5 inline-flex" data-testid="dash-go-org">
                Go to your organization <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="border border-slate-200 bg-white p-8 text-[15px] leading-relaxed text-slate-500">
              Your account doesn’t have an exam enabled yet. Ask whoever shared your access code to add one,
              and your guided plan shows up here.
            </div>
          )
        ) : (
          (user.exams || []).map((exam) => <ExamMove key={exam} exam={exam} />)
        )}
      </div>

      <div className="mt-8 flex items-center gap-4 text-sm">
        {(user.exams || []).map((exam) => (
          <Link key={exam} to={`/${exam}/progress`} className="inline-flex items-center gap-1 font-medium text-slate-500 hover:text-slate-900">
            <Compass className="h-4 w-4" /> {EXAM_NAME[exam]} full progress
          </Link>
        ))}
      </div>
    </AppShell>
  );
}
